import styles from "./QuickBooksSetup.module.css";
import {
  Image,
  Group,
  Modal,
  Title,
  Flex,
  Stack,
  Text,
  Checkbox,
  Button,
  List,
  ScrollArea,
  Select,
  Switch,
  Table,
} from "@mantine/core";
import QuickBooksLogo from "@/app/_media/quickbooks.svg";
import NextImage from "next/image";
import { useEffect, useState } from "react";
import { QBBankAccount, QBTaxGroup } from "@/app/@types/quickbooks";

import QBSyncSwitch from "@/app/_components/QBSyncSwitch/QBSyncSwitch";
import useUpdateUserQbSetup from "@/app/_hooks/quickbooks/useUpdateUserQbSetup";
import { useSetAlert } from "@/app/_hooks/useAlertContext";
import { Span } from "next/dist/trace/trace";

interface QuickbooksSetupProps {
  isOpen: boolean;
  onClose: () => void;
  taxGroups: QBTaxGroup[];
  bankAccounts: QBBankAccount[];
}

const tableRows = [
  {
    invoiceTotal: 100,
    processingFee: 2.99,
    invoiceTotalWithProcessingFee: 102.99,
    quickbooksProcessingFee: 3.08,
    processingFeeGained: 2.99 - (3.08 - 2.99),
    processingFeeLost: 3.08 - 2.99,
    percentageDifference: 0.09,
  },
  {
    invoiceTotal: 500,
    processingFee: 14.95,
    invoiceTotalWithProcessingFee: 514.95,
    quickbooksProcessingFee: 15.38,
    processingFeeGained: 14.95 - (15.38 - 14.95),
    processingFeeLost: 15.38 - 14.95,
    percentageDifference: 0.086,
  },
  {
    invoiceTotal: 1000,
    processingFee: 29.9,
    invoiceTotalWithProcessingFee: 1029.9,
    quickbooksProcessingFee: 30.77,
    processingFeeGained: 29.9 - (30.77 - 29.9),
    processingFeeLost: 30.77 - 29.9,
    percentageDifference: 0.087,
  },
  {
    invoiceTotal: 5000,
    processingFee: 149.5,
    invoiceTotalWithProcessingFee: 5149.5,
    quickbooksProcessingFee: 153.37,
    processingFeeGained: 149.5 - (153.37 - 149.5),
    processingFeeLost: 153.37 - 149.5,
    percentageDifference: 0.077,
  },
  {
    invoiceTotal: 10000,
    processingFee: 299,
    invoiceTotalWithProcessingFee: 10299,
    quickbooksProcessingFee: 307.9,
    processingFeeGained: 299 - (307.9 - 299),
    processingFeeLost: 307.9 - 299,
    percentageDifference: 0.089,
  },
  {
    invoiceTotal: 50000,
    processingFee: 1495,
    invoiceTotalWithProcessingFee: 51495,
    quickbooksProcessingFee: 1539.7,
    processingFeeGained: 1495 - (1539.7 - 1495),
    processingFeeLost: 1539.7 - 1495,
    percentageDifference: 0.089,
  },
  {
    invoiceTotal: 100000,
    processingFee: 2990,
    invoiceTotalWithProcessingFee: 102990,
    quickbooksProcessingFee: 3079.4,
    processingFeeGained: 2990 - (3079.4 - 2990),
    processingFeeLost: 3079.4 - 2990,
    percentageDifference: 0.089,
  },
];

const QuickbooksSetup = ({
  isOpen,
  onClose,
  taxGroups,
  bankAccounts,
}: QuickbooksSetupProps) => {
  const setAlert = useSetAlert();
  const [selectedTaxGroup, setSelectedTaxGroup] = useState<QBTaxGroup | null>(
    null
  );
  const [selectedBankAccount, setSelectedBankAccount] =
    useState<QBBankAccount | null>(null);
  const [isDisabled, setIsDisabled] = useState(false);
  const [stage, setStage] = useState<
    1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11
  >(1);
  const [showAnimation, setShowAnimation] = useState(false);

  const handleContinue = () => {
    setStage((prev: any) => prev + 1);
  };

  const handleBack = () => {
    setStage((prev: any) => {
      if (prev === 1) {
        return prev;
      }
      return prev - 1;
    });
  };

  const handleFinish = async () => {
    handleUpdateUserQbSetup({
      qbTaxGroup: selectedTaxGroup,
      qbBankAccount: selectedBankAccount,
    });
  };

  const {
    mutate: handleUpdateUserQbSetup,
    isPending: isUpdatingQbSetup,
    isError: isUpdateQbSetupError,
    isSuccess: isUpdateQbSetupSuccess,
  } = useUpdateUserQbSetup();

  useEffect(() => {
    if (isUpdateQbSetupSuccess) {
      const url = new URL(window.location.href);
      url.searchParams.delete("quickbooks");
      window.history.replaceState({}, "", url.toString());

      onClose();
      setShowAnimation(true);
      const timer = setTimeout(() => {
        setShowAnimation(false);
      }, 3000);
      return () => clearTimeout(timer);
    } else if (isUpdateQbSetupError) {
      setAlert({
        type: "error",
        title: "Error setting up QuickBooks",
        content:
          "Please try again, if the error persists please contact support.",
      });
    }
  }, [isUpdateQbSetupSuccess, isUpdateQbSetupError]);

  useEffect(() => {
    if (stage === 3) {
      if (selectedBankAccount) {
        setIsDisabled(false);
      } else {
        setIsDisabled(true);
      }
    } else if (stage === 4) {
      if (selectedTaxGroup) {
        setIsDisabled(false);
      } else {
        setIsDisabled(true);
      }
    } else {
      setIsDisabled(false);
    }
  }, [selectedTaxGroup, selectedBankAccount, stage]);

  return (
    <>
      <Modal
        zIndex={1000000000}
        opened={isOpen}
        onClose={onClose}
        centered
        closeOnClickOutside={false}
        closeOnEscape={false}
        withCloseButton={false}
        trapFocus={false}
        size={stage === 10 ? "xl" : "md"}
        title={
          <Group>
            <Image
              component={NextImage}
              src={QuickBooksLogo}
              alt="QuickBooks connected"
              maw={32}
              mah={32}
              width={32}
              height={32}
              radius="md"
              style={{ objectFit: "contain" }}
            />
            <Title order={2}>QuickBooks Setup</Title>
          </Group>
        }
        scrollAreaComponent={ScrollArea.Autosize}
      >
        <Flex direction="column">
          {/* Explainer */}
          {stage === 1 && (
            <Flex>
              <Stack>
                <Text fw={700} size="lg">
                  MountMonitor is now connected to QuickBooks 🎉
                </Text>
                <Text>
                  However, we still need to get a couple more things synced up.
                </Text>

                <Text>
                  The following steps will guide you in correctly configuring
                  your integration settings.
                </Text>
                <Text>
                  You&apos;ll also learn how the syncing process between
                  MountMonitor and your QuickBooks account operates, ensuring a
                  smooth and seamless flow of data between both.
                </Text>
              </Stack>
            </Flex>
          )}
          {/* Income Account */}
          {stage === 2 && (
            <Stack>
              <Text fw={700} size="lg">
                Income Account
              </Text>
              <Text>
                A new service/fee income account called{" "}
                <Text span fw={700}>
                  MountMonitor Income
                </Text>{" "}
                has been created in QuickBooks to track all revenue generated
                from jobs synced via MountMonitor.
              </Text>
              <List p="sm" pt={0} mr="md" spacing="sm">
                <List.Item>
                  <Text>
                    Items for invoices will be created in this income account in
                    the format of:
                  </Text>
                  <List p="sm" mr="md" spacing="xs">
                    <List.Item>
                      <Text span fw={700}>
                        Mount:
                      </Text>{" "}
                      Specimen name + Mount Type e.g. &quot;White-Tailed Deer
                      Buck Shoulder&quot;
                    </List.Item>
                    <List.Item>
                      <Text span fw={700}>
                        Service:
                      </Text>{" "}
                      Service Name e.g. &quot;Detachable Antlers&quot;
                    </List.Item>
                  </List>
                </List.Item>
                <List.Item>
                  <Text>
                    <Text span fw={700}>
                      Do not modify
                    </Text>{" "}
                    these items directly in QuickBooks. They are managed by
                    MountMonitor to ensure consistency between jobs and
                    invoices.
                  </Text>
                  <Text mt="xs">
                    Updating these manually in QuickBooks{" "}
                    <Text span fw={700}>
                      will cause syncing errors
                    </Text>{" "}
                    with MountMonitor.
                  </Text>
                </List.Item>
              </List>
            </Stack>
          )}
          {/* Bank Account */}
          {stage === 3 && (
            <Flex>
              <Stack w="100%">
                <Text fw={700} size="lg">
                  Bank Account
                </Text>

                <Select
                  description={
                    <Text span size="sm" c="dimmed">
                      Note: This is your QuickBooks Bank Account
                    </Text>
                  }
                  placeholder="Select bank account"
                  size="lg"
                  data={bankAccounts.map((bankAccount) => ({
                    value: bankAccount.id,
                    label: bankAccount.name,
                  }))}
                  value={selectedBankAccount?.id}
                  onChange={(value) => {
                    setSelectedBankAccount(
                      bankAccounts.find((ba) => ba.id === value) || null
                    );
                  }}
                  comboboxProps={{ zIndex: 20000000000 }}
                  w="100%"
                  width="100%"
                  checkIconPosition="right"
                />

                <Text>
                  When you mark a job’s payment status in MountMonitor as
                  <Text span fw={700}>
                    {" "}
                    Deposit Received
                  </Text>
                  ,{" "}
                  <Text span fw={700}>
                    {" "}
                    Partial Payment
                  </Text>{" "}
                  or{" "}
                  <Text span fw={700}>
                    {" "}
                    Paid in Full
                  </Text>{" "}
                  and log the payment, a matching deposit or payment record is
                  automatically created in QuickBooks.
                </Text>

                <Text>
                  The selected bank account is where these transactions will be
                  recorded against.
                </Text>
              </Stack>
            </Flex>
          )}
          {/* Tax Rate */}
          {stage === 4 && (
            <Stack>
              <Text fw={700} size="lg">
                Tax Rate
              </Text>
              <Text>
                Please select the correct tax rate that MountMonitor should use.
              </Text>
              <Text size="sm" c="dimmed">
                Note: If your tax rate is made up of multiple tax rates, the
                combined tax rate will be shown in MountMonitor. Don&apos;t
                worry, we still respect these tax rates when creating invoices.
              </Text>
              <Stack gap="xl" my="lg">
                {taxGroups.map((taxGroup) => (
                  <Stack key={taxGroup.taxGroupId}>
                    <Checkbox
                      label={`${taxGroup.taxGroupName} - ${taxGroup.totalRate}%`}
                      checked={
                        selectedTaxGroup?.taxGroupId === taxGroup.taxGroupId
                      }
                      onChange={(e) => {
                        setSelectedTaxGroup(e.target.checked ? taxGroup : null);
                      }}
                    />
                    <Stack ml="xl">
                      {taxGroup.taxRates.map((taxRate) => (
                        <Checkbox
                          disabled
                          key={taxRate.taxRateId}
                          label={`${taxRate.taxRateName} - ${taxRate.taxRate}%`}
                          checked
                        />
                      ))}
                    </Stack>
                  </Stack>
                ))}
              </Stack>

              <Text>
                We&apos;ll verify your selected tax rate with QuickBooks each
                time you log in and update MountMonitor if needed. Deactivating
                the tax rate{" "}
                <Text span fw={700}>
                  will cause syncing errors
                </Text>{" "}
                — please contact us for major tax changes.
              </Text>
            </Stack>
          )}
          {/* Discounts & Deposits */}
          {stage === 5 && (
            <Flex>
              <Stack>
                <Text size="lg" fw={700}>
                  Discounts & Deposits
                </Text>
                <Text>
                  MountMonitor requires discounts and deposits to be turns on
                  for invoices in your QuickBooks Account settings.
                </Text>
                <Text>Discount and deposit settings can be found under:</Text>
                <List spacing="sm">
                  <List.Item fw={700}>
                    Account settings {">"} Sales {">"} Sales form content
                  </List.Item>
                </List>
                <Text>
                  But don&apos;t worry, we&apos;ve ensured these are enabled for
                  you.
                </Text>

                <Stack>
                  <Switch
                    label="Discount"
                    checked
                    color="green"
                    style={{ pointerEvents: "none" }}
                  />
                  <Switch
                    label="Deposit"
                    checked
                    color="green"
                    style={{ pointerEvents: "none" }}
                  />
                </Stack>

                <Text>
                  Turning these off{" "}
                  <Text span fw={700}>
                    will cause syncing errors
                  </Text>
                  .
                </Text>
              </Stack>
            </Flex>
          )}
          {stage === 6 && (
            <Flex>
              <Stack>
                <Text size="lg" fw={700}>
                  How syncing works for clients:
                </Text>
                <Text>
                  When you create or update a client in MountMonitor,
                  you&apos;ll see a new &quot;Sync to QuickBooks&quot; toggle
                  option.
                </Text>
                <Stack>
                  <QBSyncSwitch
                    isVisible={true}
                    inputProps={{}}
                    checked={true}
                  />
                </Stack>
                <Text>
                  When enabled, the client in MountMonitor will be created or
                  updated as a customer in QuickBooks. If the customer already
                  exists in QuickBooks, we&apos;ll display any differences
                  before proceeding with the sync.
                </Text>
                <Stack>
                  <QBSyncSwitch
                    isVisible={true}
                    inputProps={{}}
                    checked={false}
                  />
                </Stack>
                <Text>
                  If the toggle is disabled, the client will not be created in
                  QuickBooks. For clients that were previously synced, disabling
                  the toggle will prevent any future changes in MountMonitor
                  from being synced to QuickBooks.
                </Text>
                <Text>
                  In order to sync jobs and automatically create invoices in
                  QuickBooks, a client must be synced.
                </Text>
              </Stack>
            </Flex>
          )}
          {stage === 7 && (
            <Flex>
              <Stack>
                <Text size="lg" fw={700}>
                  How syncing works for jobs:
                </Text>
                <Text>
                  When you select a client on a new job, we will check if they
                  are synced to QuickBooks.
                </Text>
                <Text>
                  If they are synced, you will be able to sync the job to
                  QuickBooks.
                </Text>
                <Stack>
                  <QBSyncSwitch
                    isVisible={true}
                    inputProps={{}}
                    checked={true}
                  />
                </Stack>
                <Text>
                  If you want to keep a job off QuickBooks, you can disable the
                  sync toggle.
                </Text>
                <Stack>
                  <QBSyncSwitch
                    isVisible={true}
                    inputProps={{}}
                    checked={false}
                  />
                </Stack>
                <Text>
                  Syncing a job creates an invoice in QuickBooks. Any updates to
                  the job in MountMonitor will also update the corresponding
                  invoice in QuickBooks.
                </Text>
              </Stack>
            </Flex>
          )}
          {stage === 8 && (
            <Flex>
              <Stack>
                <Text size="lg" fw={700}>
                  How syncing works for invoices:
                </Text>

                <Text>
                  When payments are logged in MountMonitor, a corresponding
                  deposit or payment record is created in QuickBooks, updating
                  the invoice and associating the transaction with the linked
                  bank account.
                </Text>
                <Text>
                  When payments are collected directly through QuickBooks,
                  whether manually by you or from an online invoice paid by your
                  customer, a secure notification is sent to our server. We then
                  update the job in MountMonitor, record the payment, and adjust
                  the payment status as needed.
                </Text>
                <Text>
                  Either way, your MountMonitor records stay in sync with
                  QuickBooks.
                </Text>
              </Stack>
            </Flex>
          )}
          {stage === 9 && (
            <Flex>
              <Stack>
                <Text size="lg" fw={700}>
                  What we don&apos;t sync:
                </Text>

                <List p="sm" pt={0} mr="md" spacing="sm">
                  <List.Item>
                    <Text>
                      Manually updating a customer in QuickBooks will not update
                      the corresponding client in MountMonitor.
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text>
                      Manually altering prices, discounts, deposits, line items,
                      or taxes directly on an invoice in QuickBooks will not
                      sync these changes back to the job details in
                      MountMonitor.
                    </Text>
                  </List.Item>
                </List>
              </Stack>
            </Flex>
          )}

          {stage === 10 && (
            <Flex>
              <Stack>
                <Text size="lg" fw={700}>
                  Processing Fees:
                </Text>

                <List p="sm" pt={0} mr="md" spacing="sm">
                  <Text>
                    QuickBooks does not support processing fees natively.
                    <br />
                    <br />
                    A common method for handling processing fees in QuickBooks
                    is to add a line item to the invoice for the fee, which is
                    what we do for you when you add a processing fee service
                    item or including a processing fee on a payment.
                    <br />
                    <br />
                    However, QuickBooks will still take their processing fee
                    from the total invoice amount. There is no way around this
                    with current QuickBooks limitations.
                    <br />
                    <br />
                    Below is a table that outlines how this method helps you
                    gain back processing fees for different amounts.
                    <br />
                    <br />
                    <Text span c="dimmed" size="sm">
                      Note: QuickBooks default online payment CC processing fee
                      rate = 2.99%
                    </Text>{" "}
                    <br />
                    <br />
                  </Text>
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Invoice Amount</Table.Th>
                        <Table.Th>Processing Fee (2.99%)</Table.Th>
                        <Table.Th>Invoice Amount + Processing Fee</Table.Th>
                        <Table.Th>QB Processing Fee Applied</Table.Th>
                        <Table.Th>Fees Absorbed</Table.Th>
                        <Table.Th>Fees Gained</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {tableRows.map((row) => (
                        <Table.Tr key={row.invoiceTotal.toFixed(2)}>
                          <Table.Td>${row.invoiceTotal}</Table.Td>
                          <Table.Td>${row.processingFee.toFixed(2)}</Table.Td>
                          <Table.Td>
                            ${row.invoiceTotalWithProcessingFee.toFixed(2)}
                          </Table.Td>
                          <Table.Td>
                            ${row.quickbooksProcessingFee.toFixed(2)}
                          </Table.Td>

                          <Table.Td>
                            ${row.processingFeeLost.toFixed(2)}
                          </Table.Td>
                          <Table.Td>
                            ${row.processingFeeGained.toFixed(2)}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                  <Text>
                    <br />
                    Ultimately, with this method, MountMonitor allows you to get
                    paid faster via online QB payments, while still gaining back
                    large amounts of processing fees.
                  </Text>
                </List>
              </Stack>
            </Flex>
          )}

          {stage === 11 && (
            <Flex>
              <Stack>
                <Text>
                  We understand that handling finances and taxes can be complex,
                  and every business has unique requirements.
                </Text>
                <Text>
                  Rest assured, MountMonitor strives to keep your financial data
                  in sync with QuickBooks. However, since the integration works
                  both ways, we don&apos;t have full control over changes made
                  directly in QuickBooks.
                </Text>

                <Text>
                  Please note that, as per our end user license agreement, the
                  responsibility for ensuring the accuracy of invoices, taxes,
                  and payments ultimately rests with you and your financial
                  advisor.
                </Text>
              </Stack>
            </Flex>
          )}

          <Group mt="lg" w="100%" justify="flex-end">
            {stage > 1 ? (
              <Button size="md" variant="default" onClick={handleBack}>
                Back
              </Button>
            ) : null}
            <Button
              type="submit"
              size="md"
              disabled={isDisabled}
              loading={isUpdatingQbSetup}
              onClick={stage === 10 ? handleFinish : handleContinue}
            >
              {stage === 1 ? "Continue" : stage === 10 ? "Finish" : "Next"}
            </Button>
          </Group>
        </Flex>
      </Modal>
      {showAnimation && (
        <Image
          className={styles.quickbooksLogo}
          component={NextImage}
          src={QuickBooksLogo}
          alt="QuickBooks connected"
          maw={64}
          mah={64}
          width={64}
          height={64}
          radius="md"
        />
      )}
    </>
  );
};

export default QuickbooksSetup;
